import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { Plus, X, Terminal, ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/utils'
import TerminalPanel from './TerminalPanel'
import { createAppSession } from '../../lib/api'
import type { AppSession } from '../../lib/api'
import { useTerminalActivity } from '../../hooks/useTerminalActivity'

interface TerminalTab {
  id: string
  sessionName: string
  title: string
}

interface TerminalTabsProps {
  appName: string
  initialSession?: string
  sessions?: AppSession[]
  onSessionChange?: (session: string) => void
  onSendDataRef?: React.MutableRefObject<((data: string) => void) | null>  // Ref for external data injection (MobileKeyboardBar)
  requestedSession?: string  // External session switch request (from session dropdown)
}

interface SavedTabState {
  tabs: { sessionName: string; title: string }[]
  activeSession: string
}

const MAX_TABS = 10

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function getStorageKey(appName: string): string {
  return `terminal-tabs-${appName}`
}

function loadSavedState(appName: string): SavedTabState | null {
  try {
    const raw = localStorage.getItem(getStorageKey(appName))
    if (raw) {
      return JSON.parse(raw)
    }
  } catch {
    // Ignore parse errors
  }
  return null
}

function saveTabs(appName: string, tabs: TerminalTab[], activeTabId: string) {
  try {
    const activeTab = tabs.find(t => t.id === activeTabId)
    const state: SavedTabState = {
      tabs: tabs.map(t => ({ sessionName: t.sessionName, title: t.title })),
      activeSession: activeTab?.sessionName || 'main',
    }
    localStorage.setItem(getStorageKey(appName), JSON.stringify(state))
  } catch {
    // Ignore storage errors
  }
}

export default function TerminalTabs({
  appName,
  initialSession = 'main',
  onSessionChange,
  onSendDataRef,
  requestedSession,
}: TerminalTabsProps) {
  // Initialize tabs from localStorage or defaults
  const [tabs, setTabs] = useState<TerminalTab[]>(() => {
    const saved = loadSavedState(appName)
    if (saved && saved.tabs.length > 0) {
      // Ensure main tab exists
      const hasMain = saved.tabs.some(t => t.sessionName === 'main')
      const restoredTabs = saved.tabs.map(t => ({
        id: generateId(),
        sessionName: t.sessionName,
        title: t.title,
      }))
      if (!hasMain) {
        restoredTabs.unshift({ id: generateId(), sessionName: 'main', title: 'main' })
      }
      return restoredTabs
    }
    return [{ id: generateId(), sessionName: initialSession, title: initialSession }]
  })

  const [activeTabId, setActiveTabId] = useState<string>(() => {
    const saved = loadSavedState(appName)
    if (saved && saved.activeSession) {
      // Find the tab matching the saved active session
      // (tabs are initialized from saved state above, so we need to match by session name)
      // Since IDs are regenerated, we'll set activeTabId in an effect below
    }
    return tabs[0]?.id || ''
  })

  // Counter for generating session names
  const tabCounter = useRef(
    tabs.reduce((max, t) => {
      const match = t.sessionName.match(/^tab-(\d+)$/)
      return match ? Math.max(max, parseInt(match[1], 10)) : max
    }, 0)
  )

  // Creating state for inline new-tab flow
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Active tab sendData ref for MobileKeyboardBar integration
  const activeSendDataRef = useRef<((data: string) => void) | null>(null)

  // Wire active terminal sendData to parent's onSendDataRef.
  useEffect(() => {
    if (!onSendDataRef) return
    onSendDataRef.current = (data: string) => {
      activeSendDataRef.current?.(data)
    }
    return () => {
      if (onSendDataRef) {
        onSendDataRef.current = null
      }
    }
  }, [onSendDataRef])

  // Activity polling for tab badges
  const sessionNames = useMemo(() => tabs.map(t => t.sessionName), [tabs])
  const activities = useTerminalActivity(appName, sessionNames)

  // Set active tab from saved state on mount
  useEffect(() => {
    const saved = loadSavedState(appName)
    if (saved && saved.activeSession) {
      const matchingTab = tabs.find(t => t.sessionName === saved.activeSession)
      if (matchingTab) {
        setActiveTabId(matchingTab.id)
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Save to localStorage on tab changes
  useEffect(() => {
    saveTabs(appName, tabs, activeTabId)
  }, [appName, tabs, activeTabId])

  // Keep active tab valid if tab list changes.
  useEffect(() => {
    if (tabs.length === 0) return
    if (!tabs.some(t => t.id === activeTabId)) {
      setActiveTabId(tabs[0].id)
    }
  }, [tabs, activeTabId])

  // Notify parent of active session change
  useEffect(() => {
    const activeTab = tabs.find(t => t.id === activeTabId)
    if (activeTab && onSessionChange) {
      onSessionChange(activeTab.sessionName)
    }
  }, [activeTabId, tabs, onSessionChange])

  // Handle external session switch requests from the session dropdown
  const prevRequestedSession = useRef<string | undefined>(undefined)
  const [isExternalSwitching, setIsExternalSwitching] = useState(false)

  useEffect(() => {
    // Only trigger when requestedSession actually changes to a new value
    if (
      !requestedSession ||
      requestedSession === prevRequestedSession.current ||
      isExternalSwitching
    ) {
      return
    }
    prevRequestedSession.current = requestedSession

    // Check if a tab with this session already exists
    const existingTab = tabs.find(t => t.sessionName === requestedSession)
    if (existingTab) {
      // Activate existing tab (no duplicate)
      setActiveTabId(existingTab.id)
      return
    }

    // No existing tab -- create a new one for this session
    if (tabs.length >= MAX_TABS) return

    setIsExternalSwitching(true)

    const switchSession = async () => {
      try {
        await createAppSession(appName, requestedSession)
      } catch {
        // Session may already exist on the backend (just not as a tab) -- proceed anyway
      }

      const newTab: TerminalTab = {
        id: generateId(),
        sessionName: requestedSession,
        title: requestedSession,
      }
      setTabs(prev => [...prev, newTab])
      setActiveTabId(newTab.id)
      setIsExternalSwitching(false)
    }

    switchSession().catch(() => {
      setIsExternalSwitching(false)
    })
  }, [requestedSession, tabs, appName, isExternalSwitching])

  // Activate a tab
  const activateTab = useCallback((tabId: string) => {
    setActiveTabId(tabId)
  }, [])

  // Create a new tab
  const createTab = useCallback(async () => {
    if (tabs.length >= MAX_TABS) return
    if (isCreating) return

    setIsCreating(true)
    setCreateError(null)

    tabCounter.current += 1
    const sessionName = `tab-${tabCounter.current}`

    try {
      await createAppSession(appName, sessionName)
      const newTab: TerminalTab = {
        id: generateId(),
        sessionName,
        title: sessionName,
      }
      setTabs(prev => [...prev, newTab])
      setActiveTabId(newTab.id)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create session'
      setCreateError(msg)
      // Clear error after 3 seconds
      setTimeout(() => setCreateError(null), 3000)
    } finally {
      setIsCreating(false)
    }
  }, [appName, tabs.length, isCreating])

  // Close a tab
  const closeTab = useCallback((tabId: string) => {
    const tab = tabs.find(t => t.id === tabId)
    if (!tab || tab.sessionName === 'main') return // Never close main

    setTabs(prev => {
      const remaining = prev.filter(t => t.id !== tabId)
      // If closing the active tab, switch to the nearest remaining tab
      if (activeTabId === tabId) {
        const closedIndex = prev.findIndex(t => t.id === tabId)
        const nextIndex = Math.min(closedIndex, remaining.length - 1)
        setActiveTabId(remaining[nextIndex]?.id || remaining[0]?.id || '')
      }
      return remaining
    })
  }, [tabs, activeTabId])

  // Keyboard shortcuts: Ctrl+1 through Ctrl+9 for tab switching
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return

      const digit = parseInt(e.key, 10)
      if (digit >= 1 && digit <= 9) {
        const tabIndex = digit - 1
        if (tabIndex < tabs.length) {
          e.preventDefault()
          activateTab(tabs[tabIndex].id)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [tabs, activateTab])

  // Collapse state for tab bar — persisted in localStorage
  const [tabsCollapsed, setTabsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('terminal-tabs-collapsed') === 'true'
    } catch {
      return false
    }
  })

  const toggleCollapsed = useCallback(() => {
    setTabsCollapsed(prev => {
      const next = !prev
      try { localStorage.setItem('terminal-tabs-collapsed', String(next)) } catch { /* ignore */ }
      return next
    })
  }, [])

  const activeTab = useMemo(
    () => tabs.find(tab => tab.id === activeTabId) ?? tabs[0] ?? null,
    [tabs, activeTabId]
  )

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      {tabsCollapsed ? (
        <div className="flex items-center border-b border-border bg-background h-7 flex-shrink-0">
          
            <button title="Show all terminal tabs"
              onClick={toggleCollapsed}
              className="flex items-center gap-1 px-2 h-full text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronRight className="w-3 h-3" />
              <Terminal className="w-3 h-3" />
              <span>{tabs.find(t => t.id === activeTabId)?.title || 'terminal'}</span>
            </button>
          
        </div>
      ) : (
        <div className="flex items-center border-b border-border bg-background h-9 flex-shrink-0 overflow-x-auto">
          {tabs.map((tab, index) => (
            
              <button title={`${tab.title} (Ctrl+${index + 1})`}
                onClick={() => activateTab(tab.id)}
                className={cn(
                  'group flex items-center gap-1.5 px-3 h-full text-sm whitespace-nowrap border-r border-border/50 transition-colors',
                  tab.id === activeTabId
                    ? 'bg-secondary/50 text-foreground border-b-2 border-b-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/30'
                )}
              >
                {/* Activity indicator dot */}
                <span className={cn(
                  "w-2 h-2 rounded-full flex-shrink-0",
                  (activities[tab.sessionName] ?? 'idle') === 'working' && "bg-yellow-400 animate-pulse",
                  (activities[tab.sessionName] ?? 'idle') === 'waiting' && "bg-green-400",
                  (activities[tab.sessionName] ?? 'idle') === 'idle' && "bg-muted-foreground/30",
                )} />
                <Terminal className="w-3 h-3 flex-shrink-0" />
                <span className="truncate max-w-[120px]">{tab.title}</span>
                {/* Close button (hidden for main) */}
                {tab.sessionName !== 'main' && (
                  <X
                    className="w-3 h-3 ml-1 flex-shrink-0 opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity"
                    onClick={(e) => { e.stopPropagation(); closeTab(tab.id) }}
                  />
                )}
              </button>
            
          ))}
          {/* Add tab button */}
          
            <button title="Open a new terminal session"
              onClick={createTab}
              disabled={tabs.length >= MAX_TABS || isCreating}
              className={cn(
                'flex items-center justify-center w-8 h-full transition-colors flex-shrink-0',
                tabs.length >= MAX_TABS || isCreating
                  ? 'text-muted-foreground/30 cursor-not-allowed'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/30'
              )}
            >
              <Plus className="w-4 h-4" />
            </button>
          
          {/* Error message */}
          {createError && (
            <span className="text-xs text-destructive px-2 whitespace-nowrap">{createError}</span>
          )}
          {/* Collapse toggle — right-aligned */}
          <div className="ml-auto flex-shrink-0">
            
              <button title="Minimize the terminal tab bar"
                onClick={toggleCollapsed}
                className="flex items-center justify-center w-7 h-full text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            
          </div>
        </div>
      )}

      {/* Tab content area */}
      <div className="flex-1 relative min-h-0">
        {activeTab && (
          <div key={activeTab.id} className="absolute inset-0">
            <TerminalPanel
              appName={appName}
              tmuxSession={activeTab.sessionName}
              closeable={false}
              onSendDataRef={onSendDataRef ? activeSendDataRef : undefined}
            />
          </div>
        )}
      </div>
    </div>
  )
}
